'use client';

import { Flex } from '@ironhack/design-system2/components/flex';
import { useRouter } from 'next/navigation';
import { has, propEq, reject } from 'ramda';
import {
  type ChangeEvent,
  type ReactElement,
  Suspense,
  useCallback,
} from 'react';

import { usePageContext } from '@/contexts/page-context/use-page-context';
import { type DatoBlogCategory } from '@/lib/datocms/types';
import { sendEvent } from '@/lib/gtm';
import { getBaseUrlFromPaginationUrl } from '@/lib/utils';
import { FilterSelect, FilterSelectStateful } from '../filter-select';

export type QueryParams = { category?: string; course?: string };

type Props = {
  categories: DatoBlogCategory[];
  categoriesPlaceholder: string;
  coursesPlaceholder: string;
  defaultFilters: QueryParams;
};

const setSearchParam = (paramName: string, value: string): string => {
  const urlWithoutPage = getBaseUrlFromPaginationUrl(window.location.href);
  const urlObj = new URL(urlWithoutPage);

  if (value) {
    urlObj.searchParams.set(paramName, value);
  } else {
    urlObj.searchParams.delete(paramName);
  }

  return urlObj.href;
};

export const ArticleFilters = (props: Props): ReactElement => {
  const {
    categories,
    categoriesPlaceholder,
    coursesPlaceholder,
    defaultFilters,
  } = props;

  const router = useRouter();
  const {
    pageData: { gaCategory },
  } = usePageContext();

  const showCourse = !has('course', defaultFilters);
  const courseOptions = categories.filter(propEq('categoryType', 'course'));

  const handleCourseFilterChange = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => {
      const { value } = event.target;
      sendEvent({
        eventCategory: gaCategory,
        eventAction: 'filter course',
        eventLabel: value,
      });

      const newUrl = setSearchParam('course', value);
      void router.push(newUrl, { scroll: false });
    },
    [gaCategory, router]
  );

  const showCategory = !has('category', defaultFilters);
  const categoryOptions = reject(propEq('categoryType', 'course'), categories);

  const handleCategoryFilterChange = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => {
      const { value } = event.target;
      sendEvent({
        eventCategory: gaCategory,
        eventAction: 'filter category',
        eventLabel: value,
      });

      const newUrl = setSearchParam('category', value);
      void router.push(newUrl, { scroll: false });
    },
    [gaCategory, router]
  );

  return (
    <Flex flexWrap={['wrap', null, 'nowrap']} gap={1} w={['full', null, '50%']}>
      {showCourse && (
        <Suspense
          // Blank placeholder until search params are loaded
          fallback={<FilterSelect options={courseOptions} placeholder=" " />}
        >
          <FilterSelectStateful
            onChange={handleCourseFilterChange}
            options={courseOptions}
            paramName="course"
            placeholder={coursesPlaceholder}
          />
        </Suspense>
      )}
      {showCategory && (
        <Suspense
          // Blank placeholder until search params are loaded
          fallback={<FilterSelect options={categoryOptions} placeholder=" " />}
        >
          <FilterSelectStateful
            onChange={handleCategoryFilterChange}
            options={categoryOptions}
            paramName="catagory"
            placeholder={categoriesPlaceholder}
          />
        </Suspense>
      )}
    </Flex>
  );
};
